import React, { useState, useEffect, useRef } from 'react';
import CommandPalette from './CommandPalette';
import SnippetBrowser from './SnippetBrowser';
import ModernHeader from './ModernHeader';
import ModernSidebar from './ModernSidebar';
import SplitViewEditor from './SplitViewEditor';
import IntegratedTerminal from './IntegratedTerminal';
import ProjectTemplates from './ProjectTemplates';
import BuildDebugPanel from './BuildDebugPanel';
import UnifiedChatAssistant from './UnifiedChatAssistant';
import GitHubSyncPanel from './GitHubSyncPanel';
import BackendNotConfigured from './BackendNotConfigured';
import apiService from '../services/api';

const ProductivityDashboard = () => {
  // Core state
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const fileContentRef = useRef('');
  const [backendHealth, setBackendHealth] = useState('unknown');
  const [isSaving, setIsSaving] = useState(false);
  
  // Refs
  const editorRef = useRef(null);

  // UI state
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showSnippetBrowser, setShowSnippetBrowser] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [rightPanel, setRightPanel] = useState('ai'); // ai, build, none
  const [toast, setToast] = useState(null);

  // Mobile state
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showMobileRightPanel, setShowMobileRightPanel] = useState(false);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
    startHealthMonitoring();
  }, []);

  // Load files when project changes
  useEffect(() => {
    if (currentProject) {
      loadFiles(currentProject.id);
    } else {
      setFiles([]);
      setCurrentFile(null);
    }
  }, [currentProject]);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Toggle Command Palette (Ctrl+P or Cmd+P)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleExecuteCommand = (command) => {
    switch (command) {
      case 'save':
        handleSaveFile();
        break;
      case 'format':
        if (editorRef.current) {
          editorRef.current.format();
          showToast('Document formatted', 'success');
        }
        break;
      case 'find':
        if (editorRef.current) {
          editorRef.current.triggerAction('actions.find');
        }
        break;
      case 'replace':
        if (editorRef.current) {
          editorRef.current.triggerAction('editor.action.startFindReplaceAction');
        }
        break;
      case 'terminal':
        setShowTerminal(prev => !prev);
        break;
      case 'ai':
        setRightPanel('ai');
        if (window.innerWidth <= 768) setShowMobileRightPanel(true);
        break;
      case 'autofix':
        setRightPanel('build');
        if (window.innerWidth <= 768) setShowMobileRightPanel(true);
        showToast('Opening Auto-Fix panel...', 'info');
        break;
      case 'snippets':
        setShowSnippetBrowser(true);
        break;
      case 'build':
        setRightPanel('build');
        if (window.innerWidth <= 768) setShowMobileRightPanel(true);
        break;
      case 'settings':
        showToast('Settings coming soon', 'info');
        break;
      case 'git-status':
      case 'git-commit':
        setShowSyncPanel(true);
        break;
      case 'new-file':
        handleCreateFile();
        break;
      case 'new-project':
        handleCreateProject();
        break;
      default:
        console.log('Unknown command:', command);
    }
  };

  const startHealthMonitoring = () => {
    const checkHealth = async () => {
      const health = await apiService.checkHealth();
      setBackendHealth(health.status || 'unknown');
    };

    checkHealth();
    const interval = setInterval(checkHealth, 15000);
    return () => clearInterval(interval);
  };

  const loadProjects = async () => {
    try {
      const projectsData = await apiService.getProjects();
      setProjects(projectsData);
    } catch (error) {
      showToast('Failed to load projects', 'error');
    }
  };

  const loadFiles = async (projectId) => {
    try {
      const filesData = await apiService.getFiles(projectId);
      setFiles(filesData);
    } catch (error) {
      showToast('Failed to load files', 'error');
    }
  };

  const handleSelectProject = (project) => {
    setCurrentProject(project);
    setCurrentFile(null);
  };

  const handleSelectFile = async (file) => {
    try {
      const fileData = await apiService.getFile(file.id);
      setCurrentFile(fileData);
      fileContentRef.current = fileData.content || '';
    } catch (error) {
      showToast('Failed to load file', 'error');
    }
  };

  const handleSaveFile = async (content = null) => {
    if (!currentFile) return;
    
    const contentToSave = content !== null ? content : fileContentRef.current;

    setIsSaving(true);
    try {
      await apiService.updateFile(currentFile.id, {
        content: contentToSave
      });
      showToast('File saved successfully', 'success');

      // Update current file content
      setCurrentFile(prev => ({
        ...prev,
        content: contentToSave
      }));
      
      // If we saved a specific content, update the ref too
      if (content !== null) {
        fileContentRef.current = content;
      }
    } catch (error) {
      showToast('Failed to save file', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateProject = async () => {
    if (backendHealth !== 'healthy') {
      showToast('Backend is not reachable. Please check if the server is running.', 'error');
      return;
    }

    const name = prompt('Enter project name:');
    if (!name) return;

    try {
      const newProject = await apiService.createProject({
        name: name,
        description: '',
        language: 'javascript'
      });
      setProjects(prev => [...prev, newProject]);
      setCurrentProject(newProject);
      showToast('Project created successfully', 'success');
    } catch (error) {
      console.error('Create Project Error:', error);
      showToast(error.message || 'Failed to create project', 'error');
    }
  };

  const handleCreateFile = async () => {
    if (backendHealth !== 'healthy') {
      showToast('Backend is not reachable.', 'error');
      return;
    }

    if (!currentProject) {
      showToast('Please select a project first', 'error');
      return;
    }

    const path = prompt('Enter file path (e.g., src/App.js):');
    if (!path) return;

    try {
      const fileName = path.split('/').pop();
      const newFile = await apiService.createFile(currentProject.id, {
        name: fileName,
        path: path,
        type: 'file',  // Required field - 'file' or 'folder'
        content: '',
        language: 'javascript'
      });
      setFiles(prev => [...prev, newFile]);
      handleSelectFile(newFile);
      showToast('File created successfully', 'success');
    } catch (error) {
      console.error('Create file error:', error);
      showToast('Failed to create file', 'error');
    }
  };

  const handleUploadFile = async (file) => {
    if (!file || !currentProject) return;

    try {
      const newFile = await apiService.uploadFile(currentProject.id, file);
      setFiles(prev => [...prev, newFile]);
      handleSelectFile(newFile);
      showToast(`File "${file.name}" uploaded successfully`, 'success');
    } catch (error) {
      showToast('Failed to upload file', 'error');
    }
  };

  const handleImportGitHub = () => {
    if (backendHealth !== 'healthy') {
      showToast('Backend is not reachable.', 'error');
      return;
    }

    const url = prompt('Enter GitHub repository URL:');
    if (!url) return;

    apiService.importGitHubProject({ github_url: url })
      .then(project => {
        setProjects(prev => [...prev, project]);
        setCurrentProject(project);
        showToast('Repository imported successfully', 'success');
      })
      .catch(error => {
        console.error('Import Error:', error);
        showToast(error.message || 'Failed to import repository', 'error');
      });
  };

  const handleCreateFromTemplate = async (templateData) => {
    if (backendHealth !== 'healthy') {
      showToast('Backend is not reachable.', 'error');
      return;
    }

    try {
      // Create project
      const newProject = await apiService.createProject({
        name: templateData.name,
        description: `Created from ${templateData.template} template`,
        language: 'javascript'
      });

      // Create files
      for (const file of templateData.files) {
        await apiService.createFile(newProject.id, {
          name: file.name.split('/').pop(),
          path: file.name,
          type: 'file',  // Required field
          content: file.content,
          language: file.language
        });
      }

      // Refresh and select
      await loadProjects();
      setCurrentProject(newProject);
      showToast(`Project "${templateData.name}" created from template!`, 'success');
    } catch (error) {
      showToast('Failed to create project from template', 'error');
    }
  };

  const handlePreview = () => {
    if (!currentFile) return;

    const content = fileContentRef.current;
    const ext = currentFile.name.split('.').pop().toLowerCase();
    if (ext === 'html') {
      const blob = new Blob([content], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } else {
      showToast('Preview only available for HTML files', 'info');
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAutoFix = (fixedCode) => {
    fileContentRef.current = fixedCode;
    handleSaveFile(fixedCode);
  };

  // Show backend not configured screen if API is unavailable
  if (!apiService.isConfigured()) {
    return <BackendNotConfigured />;
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Header */}
      <ModernHeader
        currentProject={currentProject}
        currentFile={currentFile}
        onSave={() => handleSaveFile()}
        onPreview={handlePreview}
        onSettings={() => showToast('Settings coming soon', 'info')}
        backendHealth={backendHealth}
        isSaving={isSaving}
        onToggleSidebar={() => setShowMobileSidebar(!showMobileSidebar)}
        onToggleRightPanel={() => setShowMobileRightPanel(!showMobileRightPanel)}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile Sidebar Overlay */}
        {showMobileSidebar && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setShowMobileSidebar(false)}
          />
        )}

        {/* Sidebar - Hidden on mobile, slide in when toggled */}
        <div className={`
          ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          fixed md:relative
          w-80 flex-shrink-0
          z-50 md:z-0
          transition-transform duration-300 ease-in-out
          h-full
        `}>
          <ModernSidebar
            projects={projects}
            currentProject={currentProject}
            files={files}
            currentFile={currentFile}
            onSelectProject={(project) => {
              handleSelectProject(project);
              setShowMobileSidebar(false); // Close on mobile after selection
            }}
            onSelectFile={(file) => {
              handleSelectFile(file);
              setShowMobileSidebar(false); // Close on mobile after selection
            }}
            onCreateProject={handleCreateProject}
            onCreateFile={handleCreateFile}
            onUploadFile={handleUploadFile}
            onImportGitHub={handleImportGitHub}
            onOpenTemplates={() => setShowTemplates(true)}
            onOpenSync={() => setShowSyncPanel(true)}
          />
        </div>

        {/* Editor - Full width on mobile */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-hidden">
            <SplitViewEditor
              ref={editorRef}
              file={currentFile}
              onContentChange={(content) => {
                fileContentRef.current = content;
              }}
              onSave={handleSaveFile}
            />
          </div>

          {/* Terminal - Collapsible on mobile */}
          <IntegratedTerminal
            currentProject={currentProject}
            isVisible={showTerminal}
            onToggle={() => setShowTerminal(!showTerminal)}
          />
        </div>

        {/* Mobile Right Panel Overlay */}
        {showMobileRightPanel && rightPanel !== 'none' && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setShowMobileRightPanel(false)}
          />
        )}

        {/* Right Panel - Hidden on mobile by default, slide in when toggled */}
        {rightPanel !== 'none' && (
          <div className={`
            ${showMobileRightPanel ? 'translate-x-0' : 'translate-x-full'}
            md:translate-x-0
            fixed md:relative right-0
            w-full sm:w-96 flex-shrink-0
            border-l border-gray-700
            z-50 md:z-0
            transition-transform duration-300 ease-in-out
            h-full
            hidden md:block
          `}>
            {rightPanel === 'ai' && (
              <UnifiedChatAssistant
                project={currentProject}
                files={files}
                currentFile={currentFile}
                selectedCode={''}
                variant="embedded"
                onCodeGenerated={(code) => {
                  const newContent = fileContentRef.current + '\n' + code;
                  fileContentRef.current = newContent;
                  handleSaveFile(newContent);
                }}
              />
            )}
            {rightPanel === 'build' && (
              <BuildDebugPanel
                currentProject={currentProject}
                currentFile={currentFile}
                onAutoFix={handleAutoFix}
              />
            )}
          </div>
        )}
      </div>

      {/* Bottom Toolbar */}
      <div className="h-8 bg-gray-950 border-t border-gray-700 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowTerminal(!showTerminal)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
              showTerminal ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Terminal
          </button>

          <div className="flex items-center gap-1 bg-gray-800 rounded p-0.5">
            <button
              onClick={() => setRightPanel('ai')}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                rightPanel === 'ai' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              AI
            </button>
            <button
              onClick={() => setRightPanel('build')}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                rightPanel === 'build' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Build
            </button>
            <button
              onClick={() => setRightPanel('none')}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                rightPanel === 'none' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              None
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>{files.length} files</span>
          <span>•</span>
          <span>Code Wizard v2.1</span>
        </div>
      </div>

      {/* Modals */}
      {showTemplates && (
        <ProjectTemplates
          onClose={() => setShowTemplates(false)}
          onCreateFromTemplate={handleCreateFromTemplate}
        />
      )}

      {/* GitHub Sync Panel */}
      {showSyncPanel && currentProject && (
        <GitHubSyncPanel
          project={currentProject}
          onClose={() => setShowSyncPanel(false)}
          onSyncComplete={(result) => {
            showToast(`Successfully synced ${result.files_committed} files!`, 'success');
            loadProjects();
            loadFiles(currentProject.id);
          }}
        />
      )}

      {/* Command Palette */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        files={files}
        onSelectFile={(file) => {
          handleSelectFile(file);
          setShowCommandPalette(false);
        }}
        onExecuteCommand={handleExecuteCommand}
      />

      {/* Snippet Browser */}
      <SnippetBrowser
        isOpen={showSnippetBrowser}
        onClose={() => setShowSnippetBrowser(false)}
        onInsertSnippet={(code) => {
          if (editorRef.current) {
            editorRef.current.insertText(code);
            showToast('Snippet inserted', 'success');
          }
        }}
      />

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-20 right-4 z-50">
          <div className={`px-6 py-3 rounded-lg shadow-lg text-white ${
            toast.type === 'success' ? 'bg-green-600' :
            toast.type === 'error' ? 'bg-red-600' :
            'bg-blue-600'
          }`}>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductivityDashboard;
